import moment from "moment";
import { isBlank } from "./data-utils";

const DASH = "—";

// Columns for the per-applicant "Current Loan Obligation" table — driven through
// the generic PortfolioTable. `money: true` formats values in rupees with the
// active unit toggle (Lacs/Cr/Full).
//
// The identity columns (through Ownership) are frozen — see BUREAU_LOAN_STICKY
// below — so they need explicit widths for the table to compute their left
// offsets. Everything after Ownership scrolls horizontally.
export const BUREAU_LOAN_COLUMNS = [
    { key: "lender_name", label: "Lender", width: 170 },
    // Hovering the cell reveals the bureau's full account-type label plus the
    // Revolving/Term Loan product type, since the Product Type column below is
    // no longer shown on its own.
    {
        key: "nature_of_loan",
        label: "Nature of Loan",
        width: 140,
        tooltipKey: "nature_of_loan_tooltip"
    },
    // {
    //     key: "product_type",
    //     label: "Product Type",
    //     width: 120,
    //     tooltipKey: "account_type_name"
    // },
    // Wider than its neighbours: Ownership values run long ("Authorised User
    // (access to supplementary credit card holder)"), and this keeps the first
    // couple of words visible before the ellipsis.
    { key: "ownership_indicator_name", label: "Ownership", width: 170 },
    { key: "sanction_amount", label: "Sanction Amount", money: true },
    { key: "outstanding", label: "Current OS", money: true },
    { key: "overdue", label: "Overdue", money: true },
    {
        key: "wo_settlement_lss_amount",
        label: "WO / Settlement",
        money: true
    },
    { key: "emi_monthly_obligation", label: "Monthly EMI", money: true },
    { key: "loan_start_date_label", label: "Loan Start Date" },
    { key: "last_reported_date_label", label: "Date Reported" },
    { key: "dpd_30_plus_count_l6m", label: "DPD 30+ (6m)", number: true },
    { key: "dpd_90_plus_count_l12m", label: "DPD 90+ (12m)", number: true },
    // Bureau stress flag. Rendered rather than passed through formatCell because
    // the field's shape is not fixed — a boolean reads as Yes/No, anything else
    // (a label, a score) is shown as-is. Hover surfaces the loan's payment
    // behaviour; the tooltip field is only populated when stress is non-null.
    {
        key: "financial_stress",
        label: "Financial Stress",
        width: 130,
        tooltipKey: "financial_stress_tooltip",
        render: value => {
            if (isBlank(value)) {
                return DASH;
            }
            if (typeof value === "boolean") {
                return value ? "Yes" : "No";
            }
            return String(value);
        }
    }
];

// Number of leading columns frozen against the left edge. The section prepends
// a 1-col include checkbox and a 1-col edit action, so this counts those two
// columns plus Lender → Ownership (Product Type is commented out above).
export const BUREAU_LOAN_STICKY = 5;

// Convert API "YYYY-MM-DD" to "DD/MM/YYYY"; falls through unchanged when the
// bureau leaves it blank or in a non-standard shape.
function formatDate(raw) {
    if (!raw) {
        return null;
    }
    const m = moment(String(raw).slice(0, 10), "YYYY-MM-DD", true);
    return m.isValid() ? m.format("DD/MM/YYYY") : String(raw);
}

function toNumber(value) {
    if (isBlank(value)) {
        return 0;
    }
    const n = Number(String(value).replace(/[^0-9.-]/g, ""));
    return isFinite(n) && !Number.isNaN(n) ? n : 0;
}

// Transform one raw loan row into the view-model the table expects (adds a
// formatted date, otherwise pass-through so totals math stays simple).
function toLoanRow(raw) {
    return {
        ...raw,
        loan_start_date_label: formatDate(raw.loan_start_date),
        last_reported_date_label: formatDate(raw.date_reported),
        // Nature of Loan's hover text, now that Product Type is commented out:
        // the bureau's full account-type label + the raw product type value.
        nature_of_loan_tooltip: [raw.account_type_name, raw.product_type]
            .filter(Boolean)
            .join(" | "),
        // Financial Stress hover: the loan's payment behaviour. Left null when
        // stress itself is null/blank so the cell shows no tooltip at all.
        financial_stress_tooltip:
            isBlank(raw.financial_stress) || !raw.payment_behaviour
                ? null
                : `Payment Behaviour: ${raw.payment_behaviour}`
    };
}

// Sum sanction / outstanding / overdue / EMI across a loan list (skips bad cells).
// Exported so the section can re-total a subset of rows when the analyst
// unchecks loans, without duplicating the arithmetic.
export function sumLoans(loans) {
    return (loans || []).reduce(
        (acc, l) => ({
            sanction_amount: acc.sanction_amount + toNumber(l.sanction_amount),
            outstanding: acc.outstanding + toNumber(l.outstanding),
            overdue: acc.overdue + toNumber(l.overdue),
            emi_monthly_obligation:
                acc.emi_monthly_obligation +
                toNumber(l.emi_monthly_obligation)
        }),
        {
            sanction_amount: 0,
            outstanding: 0,
            overdue: 0,
            emi_monthly_obligation: 0
        }
    );
}

// Top-level builder used by the section: groups by applicant, decorates rows,
// computes per-applicant totals and an overall total. Returns null when the
// API surfaced no records at all (so the section can render an empty state).
export function buildBureauLoans(rawData) {
    const list = Array.isArray(rawData) ? rawData : [];
    if (!list.length) {
        return null;
    }
    const applicants = list.map(group => {
        const loans = (group.loans || []).map(toLoanRow);
        const totals = sumLoans(loans);
        return {
            applicantId: group.applicant_id,
            bureauId: group.bureau_id,
            applicantName: group.applicant_name || `Applicant ${group.applicant_id}`,
            bureauScore: group.bureau_score,
            isMainBorrower: Boolean(group.is_main_borrower),
            // When this applicant's bureau (CIBIL) was last pulled — drives the
            // "last pulled" label + the >30-day re-pull trigger in the section.
            bureauCreateDate:
                group.bureau_create_date || group.bureauCreateDate || null,
            loanCount: loans.length,
            loans,
            totals
        };
    });
    const overall = applicants.reduce(
        (acc, a) => ({
            loanCount: acc.loanCount + a.loanCount,
            sanction_amount:
                acc.sanction_amount + a.totals.sanction_amount,
            outstanding: acc.outstanding + a.totals.outstanding,
            emi_monthly_obligation:
                acc.emi_monthly_obligation + a.totals.emi_monthly_obligation
        }),
        {
            loanCount: 0,
            sanction_amount: 0,
            outstanding: 0,
            emi_monthly_obligation: 0
        }
    );
    return { applicants, overall };
}

// Exposed so the section can render a dash for missing aggregates without
// duplicating the helper.
export const FORMAT_DASH = DASH;

// ── Combined Loans + Enquiries by-type summary (mirrors the Credit Brief PDF) ──

// Account-type categories, in display order (Total appended last).
const SUMMARY_TAG_ORDER = ["USL/BL", "CC/OD", "Secured", "Gold Loans", "Others"];

// Builds the merged per-category rows for one applicant: each row carries the
// loan-summary aggregates (left "Loans" block) and enquiry-summary counts
// (right "Enquiries" block). Categories with no loan AND no enquiry data are
// dropped; the Total row is always kept.
function buildSummaryRows(loanSummary, enqSummary) {
    const rows = [];
    [...SUMMARY_TAG_ORDER, "Total"].forEach(cat => {
        const ld = (loanSummary && loanSummary[cat]) || {};
        const ed = (enqSummary && enqSummary[cat]) || {};
        const hasLoan = [
            "no_loans_l12m",
            "total_sanction_amount",
            "outstanding",
            "overdue",
            "wo_settlement_lss_amount"
        ].some(k => toNumber(ld[k]) !== 0);
        const hasEnq = [
            "l7_days",
            "l30_days",
            "l3_months",
            "l6_months",
            "gt6_months"
        ].some(k => toNumber(ed[k]) !== 0);
        if (cat !== "Total" && !hasLoan && !hasEnq) {
            return;
        }
        rows.push({
            category: cat,
            isTotal: cat === "Total",
            // Loans block
            loans3m: toNumber(ld.no_loans_l3m),
            loans12m: toNumber(ld.no_loans_l12m),
            sanction: toNumber(ld.total_sanction_amount),
            outstanding: toNumber(ld.outstanding),
            overdue: toNumber(ld.overdue),
            dpd306m: toNumber(ld.dpd_30_plus_l6m),
            dpd9012m: toNumber(ld.dpd_90_plus_l12m),
            settlement: toNumber(ld.wo_settlement_lss_amount),
            // Enquiries block
            d7: toNumber(ed.l7_days),
            d30: toNumber(ed.l30_days),
            m3: toNumber(ed.l3_months),
            m6: toNumber(ed.l6_months),
            gt6: toNumber(ed.gt6_months)
        });
    });
    return rows;
}

// Maps the v2 bureau_loan_summary + bureau_enquiry_summary lists into a
// { [applicantId]: rows } lookup. `filterKey` selects which loan variant the
// summary reflects (all_loans / progfin_loans / non_progfin_loans); enquiries
// are not filtered server-side.
export function buildBureauSummaries(loanSummaryList, enqSummaryList, filterKey) {
    const key = filterKey || "non_progfin_loans";
    const enqById = {};
    (enqSummaryList || []).forEach(a => {
        enqById[a.applicant_id] = a;
    });
    const map = {};
    (loanSummaryList || []).forEach(a => {
        const loanSummary =
            a[key] || a.all_loans || a.non_progfin_loans || a.progfin_loans || {};
        const enq = (enqById[a.applicant_id] || {}).enquiry_summary || {};
        map[a.applicant_id] = buildSummaryRows(loanSummary, enq);
    });
    // Applicants that have enquiries but no loan-summary entry still get a table.
    (enqSummaryList || []).forEach(a => {
        if (!(a.applicant_id in map)) {
            map[a.applicant_id] = buildSummaryRows({}, a.enquiry_summary || {});
        }
    });
    return map;
}
